import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import filterFactory, { textFilter } from 'react-bootstrap-table2-filter';
import {
  FaSearch, FaTools, FaTrash, FaCheckCircle, FaTruckMonster,
  FaHistory, FaSyncAlt, FaFilePdf, FaFileExcel, FaBuilding,
  FaCalendarAlt, FaTag, FaIndustry, FaHardHat
} from 'react-icons/fa';
import Select from 'react-select';
import { canAccess } from '../utils/canAccess';
import { generateMaterielReparationsPDF } from '../utils/generateMaterielReparationsPDF';
import { generateMaterielReparationsExcel } from '../utils/generateMaterielReparationsExcel';

// ── helpers ────────────────────────────────────────────────────────────────
const safeText  = (v) => (v === null || v === undefined ? '' : String(v));
const toNumber  = (v, def = 0) => { const n = parseFloat(v); return Number.isFinite(n) ? n : def; };
const fmtMoney  = (n) => (Number(n) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const toTaxRate = (v) => { const n = toNumber(v, 0); return n > 1 ? n / 100 : n; };
const fmtTaxRate = (v) => `${(toTaxRate(v) * 100).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} %`;
const fmtDateTime = (s) => { if (!s) return ''; const d = new Date(s); return isNaN(d) ? String(s) : d.toLocaleString('fr-FR'); };
const normalizeArrayResponse = (d) => Array.isArray(d) ? d : Array.isArray(d?.data) ? d.data : Array.isArray(d?.rows) ? d.rows : [];

const buildMaterielLabel = (m) => {
  const parts = [m?.matricule, m?.type, m?.societe, m?.chantier_actuel ? `Ch: ${m.chantier_actuel}` : null].filter(Boolean);
  return `#${m?.id}${parts.length ? ' — ' + parts.join(' | ') : ''}`;
};

// ── styles ─────────────────────────────────────────────────────────────────
const BROWN = '#6d4e36';
const BROWN_BG = '#f8f2ea';

const cardStyle = {
  borderRadius: 14,
  border: '1px solid rgba(109,78,54,0.18)',
  boxShadow: '0 4px 16px rgba(109,78,54,0.08)',
  marginBottom: 20
};

const headerStyle = {
  background: BROWN_BG,
  borderBottom: `2px solid rgba(109,78,54,0.15)`,
  color: BROWN,
  fontWeight: 700,
  borderRadius: '14px 14px 0 0'
};

const selectStyles = {
  control: (b) => ({ ...b, borderColor: BROWN, boxShadow: 'none', '&:hover': { borderColor: BROWN } }),
  menuPortal: (b) => ({ ...b, zIndex: 99999 }),
  menu: (b) => ({ ...b, zIndex: 99999 })
};

// ── QteInput : input non-contrôlé pour éviter perte de focus ───────────────
const QteInput = React.memo(({ lineId, defaultVal, onCommit }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current && ref.current !== document.activeElement) {
      ref.current.value = defaultVal;
    }
  }, [defaultVal]);

  return (
    <Form.Control
      ref={ref}
      size="sm"
      type="number"
      min="0"
      step="0.01"
      defaultValue={defaultVal}
      onBlur={(e)  => onCommit(lineId, e.target.value)}
      onChange={(e) => onCommit(lineId, e.target.value)}
      style={{ width: 90, textAlign: 'right' }}
    />
  );
});

// ── composant principal ────────────────────────────────────────────────────
const BdcReparationPage = ({ user }) => {
  // BDC
  const [idbdc, setIdbdc]             = useState('');
  const [rows, setRows]               = useState([]);
  const [loadingBdc, setLoadingBdc]   = useState(false);
  const [error, setError]             = useState('');
  const [successMsg, setSuccessMsg]   = useState('');

  // Sélection + qtés
  const [selectedLineIds, setSelectedLineIds] = useState([]);
  const [qteOverrides, setQteOverrides]       = useState({});

  // Matériels
  const [materiels, setMateriels]                     = useState([]);
  const [loadingMateriels, setLoadingMateriels]       = useState(false);
  const [selectedMaterielId, setSelectedMaterielId]   = useState('');
  const [selectedMaterielOption, setSelectedMaterielOption] = useState(null);

  // Validation
  const [commentaire, setCommentaire] = useState('');
  const [submitting, setSubmitting]   = useState(false);

  // Historique
  const [historyRows, setHistoryRows]       = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError]     = useState('');

  const canValidate = useMemo(() => canAccess(user, 'bdc-reparations:write'), [user]);

  // ── header BDC ────────────────────────────────────────────────────────
  const header = useMemo(() => {
    if (!rows.length) return null;
    const h = rows[0];
    return {
      IDBDC: h.IDBDC, DATE_BDC: h.DATE_BDC, REF: h.REF,
      SOCIETE_NAME: h.SOCIETE_NAME, FOURNISSEUR_LIBELLE: h.FOURNISSEUR_LIBELLE,
      IDCHANTIER: h.IDCHANTIER, CHANTIER_NAME: h.CHANTIER_NAME
    };
  }, [rows]);

  // ── QTE helpers ───────────────────────────────────────────────────────
  const getRowQte = useCallback((row) => {
    const v = qteOverrides[row?.ID_BDC_LINE];
    return v !== undefined && v !== '' ? toNumber(v, 0) : toNumber(row?.QTE, 0);
  }, [qteOverrides]);

  const handleQteCommit = useCallback((lineId, value) => {
    setQteOverrides(prev => ({ ...prev, [lineId]: value }));
  }, []);

  // ── totaux sélection ──────────────────────────────────────────────────
  const totals = useMemo(() => {
    const sel = new Set(selectedLineIds);
    return rows.filter(r => sel.has(r.ID_BDC_LINE)).reduce((acc, r) => {
      const prix = toNumber(r.PRIX);
      const qte  = getRowQte(r);
      const ttc  = prix + prix * toTaxRate(r.TAXE);
      return { totalHT: acc.totalHT + prix * qte, totalTTC: acc.totalTTC + ttc * qte };
    }, { totalHT: 0, totalTTC: 0 });
  }, [rows, selectedLineIds, getRowQte]);

  const selectedMateriel = useMemo(() => {
    const mid = parseInt(selectedMaterielId, 10);
    return materiels.find(m => parseInt(m.id, 10) === mid) || null;
  }, [selectedMaterielId, materiels]);

  const materielOptions = useMemo(() =>
    materiels.map(m => ({ value: String(m.id), label: buildMaterielLabel(m), raw: m })),
  [materiels]);

  // ── fetch matériels ───────────────────────────────────────────────────
  useEffect(() => {
    setLoadingMateriels(true);
    axios.get('/materiels')
      .then(r => setMateriels(normalizeArrayResponse(r.data)))
      .catch(() => setMateriels([]))
      .finally(() => setLoadingMateriels(false));
  }, []);

  // ── fetch historique ──────────────────────────────────────────────────
  const fetchHistory = useCallback(async (materielId) => {
    const mid = parseInt(materielId, 10);
    if (!mid) { setHistoryRows([]); return; }
    setLoadingHistory(true);
    setHistoryError('');
    try {
      const res = await axios.get('/materiel-reparations', { params: { id_materiel: mid } });
      const list = normalizeArrayResponse(res.data).map(r => {
        const prix = toNumber(r.prix ?? r.PRIX);
        const tax  = toTaxRate(r.taxe ?? r.TAXE);
        const qte  = toNumber(r.qte ?? r.QTE ?? 0);
        const ttcU = prix + prix * tax;
        return { ...r, _qte: qte, _htU: prix, _ttcU: ttcU, _htT: prix * qte, _ttcT: ttcU * qte };
      });
      setHistoryRows(list);
    } catch (e) {
      setHistoryError(e?.response?.data?.message || "❌ Erreur chargement historique.");
      setHistoryRows([]);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    if (selectedMaterielId) fetchHistory(selectedMaterielId);
    else setHistoryRows([]);
  }, [selectedMaterielId, fetchHistory]);

  // ── handlers ──────────────────────────────────────────────────────────
  const handleLoadBdc = async () => {
    setError(''); setSuccessMsg(''); setRows([]); setSelectedLineIds([]); setQteOverrides({});
    const id = parseInt(idbdc, 10);
    if (!id) { setError("⚠️ Saisis un IDBDC valide."); return; }
    setLoadingBdc(true);
    try {
      const res = await axios.get('/bdc/details', { params: { idbdc: id } });
      const data = res.data?.rows || [];
      setRows(data);
      const init = {};
      data.forEach(r => { if (r?.ID_BDC_LINE) init[r.ID_BDC_LINE] = r?.QTE ?? 0; });
      setQteOverrides(init);
      if (!data.length) setError(`❌ Aucun résultat pour IDBDC=${id}.`);
    } catch (e) {
      setError(e?.response?.data?.message || "❌ Erreur chargement BDC.");
    } finally {
      setLoadingBdc(false);
    }
  };

  const handleReset = () => {
    setError(''); setSuccessMsg(''); setRows([]); setSelectedLineIds([]);
    setCommentaire(''); setQteOverrides('');
  };

  const handleSubmit = async () => {
    setError(''); setSuccessMsg('');
    if (!canValidate) { setError("⛔ Accès refusé."); return; }
    const id  = parseInt(idbdc, 10);
    const mid = parseInt(selectedMaterielId, 10);
    if (!id)  { setError("⚠️ IDBDC invalide."); return; }
    if (!mid) { setError("⚠️ Choisis un engin."); return; }
    if (!selectedLineIds.length) { setError("⚠️ Sélectionne au moins une ligne."); return; }
    setSubmitting(true);
    try {
      const res = await axios.post('/materiel-reparations/from-bdc', {
        id_materiel: mid, idbdc: id, selectedLineIds, commentaire,
        qteMap: Object.fromEntries(selectedLineIds.map(lid => [lid, toNumber(qteOverrides?.[lid], 0)]))
      });
      const { inserted = 0, duplicates = 0, skipped = 0 } = res.data?.data || {};
      setSuccessMsg(`✅ ${inserted} insérée(s), ${duplicates} doublon(s), ${skipped} ignorée(s).`);
      setSelectedLineIds([]);
      setCommentaire('');
      await fetchHistory(mid);
    } catch (e) {
      setError(e?.response?.data?.message || "❌ Erreur validation.");
    } finally {
      setSubmitting(false);
    }
  };

  // ── colonnes table BDC ────────────────────────────────────────────────
  const bdcColumns = useMemo(() => [
    {
      dataField: 'ID_BDC_LINE', text: 'Ligne', sort: true,
      headerStyle: { width: 70 },
      filter: textFilter({ placeholder: 'ID...' })
    },
    {
      dataField: 'DESIGNATION', text: 'Désignation', sort: true,
      filter: textFilter({ placeholder: 'Rechercher...' })
    },
    {
      dataField: 'CODE_PRODUIT', text: 'Code', sort: true,
      headerStyle: { width: 100 },
      filter: textFilter({ placeholder: 'Code...' })
    },
    {
      dataField: '_qte', text: 'Qté', headerStyle: { width: 110 },
      formatter: (_, row) => (
        <QteInput
          lineId={row.ID_BDC_LINE}
          defaultVal={qteOverrides[row.ID_BDC_LINE] ?? row.QTE ?? 0}
          onCommit={handleQteCommit}
        />
      )
    },
    {
      dataField: 'UNITE', text: 'Unité', sort: true,
      headerStyle: { width: 70 },
      filter: textFilter({ placeholder: 'Unité...' })
    },
    {
      dataField: 'PRIX', text: 'Prix HT', sort: true,
      headerStyle: { width: 100 },
      formatter: (c) => fmtMoney(c),
      filter: textFilter({ placeholder: 'Prix...' })
    },
    {
      dataField: 'TAXE', text: 'TVA', sort: true,
      headerStyle: { width: 80 },
      formatter: (c) => fmtTaxRate(c)
    },
    {
      dataField: '_ttc_u', text: 'TTC unit.',
      headerStyle: { width: 100 },
      formatter: (_, row) => {
        const p = toNumber(row.PRIX); return fmtMoney(p + p * toTaxRate(row.TAXE));
      }
    },
    {
      dataField: '_ht_t', text: 'Total HT',
      headerStyle: { width: 100 },
      formatter: (_, row) => fmtMoney(toNumber(row.PRIX) * getRowQte(row))
    },
    {
      dataField: '_ttc_t', text: 'Total TTC',
      headerStyle: { width: 110 },
      formatter: (_, row) => {
        const p = toNumber(row.PRIX); const q = getRowQte(row);
        return fmtMoney((p + p * toTaxRate(row.TAXE)) * q);
      }
    },
    {
      dataField: 'LINE_SAISIE_DATE', text: 'Saisie', sort: true,
      headerStyle: { width: 110 },
      filter: textFilter({ placeholder: 'Date...' })
    }
  ], [qteOverrides, getRowQte, handleQteCommit]);

  const selectRow = useMemo(() => ({
    mode: 'checkbox', clickToSelect: false,
    selected: selectedLineIds,
    onSelect: (row, isSelect) => {
      const id = row?.ID_BDC_LINE;
      if (!id) return;
      setSelectedLineIds(prev => isSelect ? [...new Set([...prev, id])] : prev.filter(x => x !== id));
    },
    onSelectAll: (isSelect, allRows) => {
      setSelectedLineIds(isSelect ? allRows.map(r => r.ID_BDC_LINE).filter(Boolean) : []);
    }
  }), [selectedLineIds]);

  const rowStyle = (row) =>
    selectedLineIds.includes(row.ID_BDC_LINE)
      ? { backgroundColor: '#f0ebe4', fontWeight: 500 }
      : {};

  // ── colonnes historique ───────────────────────────────────────────────
  const historyColumns = useMemo(() => [
    { dataField: 'id', text: 'ID', sort: true, headerStyle: { width: 60 }, filter: textFilter({ placeholder: 'id...' }) },
    { dataField: 'created_at', text: 'Date', sort: true, formatter: (c) => fmtDateTime(c), filter: textFilter({ placeholder: 'date...' }) },
    { dataField: 'ref_bdc', text: 'Réf BDC', sort: true, filter: textFilter({ placeholder: 'ref...' }) },
    { dataField: 'fournisseur_libelle', text: 'Fournisseur', sort: true, filter: textFilter({ placeholder: 'fournisseur...' }) },
    { dataField: 'designation', text: 'Désignation', sort: true, filter: textFilter({ placeholder: 'désignation...' }) },
    { dataField: 'code_produit', text: 'Code', sort: true, filter: textFilter({ placeholder: 'code...' }) },
    { dataField: '_qte', text: 'Qté', sort: true },
    { dataField: 'unite', text: 'Unité', sort: true },
    { dataField: '_htU', text: 'Prix HT', sort: true, formatter: (c) => fmtMoney(c) },
    { dataField: '_ttcU', text: 'TTC unit.', sort: true, formatter: (c) => fmtMoney(c) },
    { dataField: '_htT', text: 'Total HT', sort: true, formatter: (c) => fmtMoney(c) },
    { dataField: '_ttcT', text: 'Total TTC', sort: true, formatter: (c) => fmtMoney(c) },
    { dataField: 'commentaire', text: 'Commentaire', filter: textFilter({ placeholder: 'commentaire...' }) }
  ], []);

  const historyTotals = useMemo(() => ({
    ht:  historyRows.reduce((s, r) => s + toNumber(r._htT), 0),
    ttc: historyRows.reduce((s, r) => s + toNumber(r._ttcT), 0)
  }), [historyRows]);

  const exportContext = useMemo(() => ({
    generatedAt: new Date().toISOString(),
    userName: user?.name || user?.login || '',
    materiel: selectedMateriel ? {
      id: selectedMateriel.id, matricule: selectedMateriel.matricule,
      type: selectedMateriel.type, societe: selectedMateriel.societe,
      chantier_actuel: selectedMateriel.chantier_actuel,
      etat: selectedMateriel.etat, observation: selectedMateriel.observation
    } : { id: selectedMaterielId },
    totals: { totalHT: historyTotals.ht, totalTTC: historyTotals.ttc, count: historyRows.length }
  }), [selectedMateriel, selectedMaterielId, historyTotals, historyRows.length, user]);

  // ── render ────────────────────────────────────────────────────────────
  return (
    <div className="container mt-4 mb-5">

      {/* ── Titre ── */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
        <div>
          <h2 className="mb-1 d-flex align-items-center gap-2" style={{ color: BROWN, fontWeight: 800 }}>
            <FaTools /> BDC → Réparations engin
          </h2>
          <div className="text-muted" style={{ fontSize: '0.9rem' }}>
            Charge un BDC, ajuste les quantités, sélectionne les lignes et valide vers l'historique de réparation.
          </div>
        </div>
        <div className="d-flex gap-2 align-items-center flex-wrap">
          <Badge style={{ background: BROWN, padding: '9px 14px', fontSize: '0.8rem' }}>
            👤 {user?.name || user?.login || 'Utilisateur'}
          </Badge>
          <Badge bg={canValidate ? 'success' : 'secondary'} style={{ padding: '9px 14px', fontSize: '0.8rem' }}>
            {canValidate ? '✅ Validation activée' : '🔒 Lecture seule'}
          </Badge>
        </div>
      </div>

      {/* ── Alerts ── */}
      {error      && <Alert variant="danger"  onClose={() => setError('')}      dismissible>{error}</Alert>}
      {successMsg && <Alert variant="success" onClose={() => setSuccessMsg('')} dismissible>{successMsg}</Alert>}

      {/* ── Recherche BDC ── */}
      <Card style={cardStyle}>
        <Card.Header style={headerStyle} className="d-flex align-items-center justify-content-between">
          <span><FaSearch className="me-2" />Charger un BDC</span>
          <Button variant="outline-secondary" size="sm" onClick={handleReset} disabled={loadingBdc || submitting}>
            <FaTrash className="me-1" /> Reset
          </Button>
        </Card.Header>
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col md={3}>
              <Form.Label className="fw-bold">N° BDC (IDBDC)</Form.Label>
              <Form.Control
                type="number"
                value={idbdc}
                onChange={e => setIdbdc(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleLoadBdc()}
                placeholder="Ex : 554"
                style={{ borderColor: BROWN }}
              />
            </Col>
            <Col md="auto">
              <Button style={{ background: BROWN, border: 'none' }} onClick={handleLoadBdc} disabled={loadingBdc}>
                {loadingBdc ? <><Spinner size="sm" className="me-2" />Chargement…</> : <><FaSearch className="me-2" />Charger</>}
              </Button>
            </Col>
            {rows.length > 0 && (
              <Col className="text-end">
                <Badge bg="dark" style={{ padding: '8px 14px' }}>📄 {rows.length} ligne(s)</Badge>
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>

      {/* ── Infos BDC ── */}
      {header && (
        <Card style={{ ...cardStyle, background: BROWN_BG }}>
          <Card.Header style={headerStyle}>📌 Informations BDC #{header.IDBDC}</Card.Header>
          <Card.Body>
            <Row className="g-3">
              <Col md={2}>
                <div className="d-flex align-items-center gap-2 text-muted mb-1"><FaCalendarAlt size={13} /> Date</div>
                <strong>{safeText(header.DATE_BDC)}</strong>
              </Col>
              <Col md={2}>
                <div className="d-flex align-items-center gap-2 text-muted mb-1"><FaTag size={13} /> Réf</div>
                <strong>{safeText(header.REF)}</strong>
              </Col>
              <Col md={3}>
                <div className="d-flex align-items-center gap-2 text-muted mb-1"><FaIndustry size={13} /> Société</div>
                <strong>{safeText(header.SOCIETE_NAME)}</strong>
              </Col>
              <Col md={3}>
                <div className="d-flex align-items-center gap-2 text-muted mb-1"><FaBuilding size={13} /> Fournisseur</div>
                <strong>{safeText(header.FOURNISSEUR_LIBELLE)}</strong>
              </Col>
              <Col md={2}>
                <div className="d-flex align-items-center gap-2 text-muted mb-1"><FaHardHat size={13} /> Chantier</div>
                <strong>{header.CHANTIER_NAME || `ID ${header.IDCHANTIER}`}</strong>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* ── Table lignes BDC ── */}
      {rows.length > 0 && (
        <Card style={cardStyle}>
          <Card.Header style={headerStyle} className="d-flex justify-content-between align-items-center">
            <span>📄 Lignes BDC</span>
            <div className="d-flex gap-3 align-items-center" style={{ fontSize: '0.85rem', fontWeight: 400, color: '#555' }}>
              <span>Sélectionnées : <strong style={{ color: BROWN }}>{selectedLineIds.length}</strong></span>
              <span>HT sélection : <strong>{fmtMoney(totals.totalHT)}</strong></span>
              <span>TTC sélection : <strong>{fmtMoney(totals.totalTTC)}</strong></span>
            </div>
          </Card.Header>
          <Card.Body style={{ padding: '12px 16px' }}>
            <div className="text-muted mb-2" style={{ fontSize: '0.82rem' }}>
              ✏️ Modifie la <strong>Qté</strong> directement dans le champ — puis coche les lignes à valider.
            </div>
            <BootstrapTable
              keyField="ID_BDC_LINE"
              data={rows}
              columns={bdcColumns}
              bootstrap4
              bordered={false}
              hover
              condensed
              rowStyle={rowStyle}
              selectRow={selectRow}
              filter={filterFactory()}
              pagination={paginationFactory({ sizePerPage: 10, showTotal: true, alwaysShowAllBtns: true })}
              noDataIndication="Aucune ligne"
              wrapperClasses="table-responsive"
            />
          </Card.Body>
        </Card>
      )}

      {/* ── Validation ── */}
      <Card style={cardStyle}>
        <Card.Header style={headerStyle} className="d-flex align-items-center justify-content-between">
          <span><FaCheckCircle className="me-2" />Valider en réparation</span>
          {selectedLineIds.length > 0 && (
            <Badge bg="warning" text="dark" style={{ padding: '7px 14px' }}>
              {selectedLineIds.length} ligne(s) sélectionnée(s)
            </Badge>
          )}
        </Card.Header>
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={5}>
              <Form.Label className="fw-bold"><FaTruckMonster className="me-2" />Engin</Form.Label>
              <Select
                value={selectedMaterielOption}
                onChange={opt => { setSelectedMaterielOption(opt || null); setSelectedMaterielId(opt?.value || ''); }}
                options={materielOptions}
                isClearable isSearchable
                placeholder={loadingMateriels ? '⏳ Chargement...' : '🔎 Rechercher un engin...'}
                noOptionsMessage={() => 'Aucun engin'}
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </Col>
            <Col md={4}>
              <Form.Label className="fw-bold">📝 Commentaire (optionnel)</Form.Label>
              <Form.Control
                value={commentaire}
                onChange={e => setCommentaire(e.target.value)}
                placeholder="Ex : Vidange + filtre…"
                disabled={!canValidate}
                style={{ borderColor: BROWN }}
              />
            </Col>
            <Col md={3}>
              <Button
                className="w-100"
                style={{ background: BROWN, border: 'none', height: 38 }}
                onClick={handleSubmit}
                disabled={submitting || !canValidate || !rows.length || !selectedLineIds.length || !selectedMaterielId}
              >
                {submitting
                  ? <><Spinner size="sm" className="me-2" />Validation…</>
                  : <><FaTools className="me-2" />Valider ({selectedLineIds.length})</>}
              </Button>
              {!canValidate && (
                <div className="text-muted mt-1" style={{ fontSize: '0.78rem' }}>
                  🔒 Réservé admin / responsable
                </div>
              )}
            </Col>
          </Row>
          <div className="text-muted mt-3" style={{ fontSize: '0.8rem' }}>
            🔁 Anti-doublon actif — même (engin + BDC + ligne) ne s'insère qu'une fois.
          </div>
        </Card.Body>
      </Card>

      {/* ── Historique ── */}
      <Card style={cardStyle}>
        <Card.Header style={headerStyle} className="d-flex justify-content-between align-items-center">
          <span>
            <FaHistory className="me-2" />
            Historique réparations
            {selectedMaterielId
              ? <span className="fw-normal text-muted ms-2">— {selectedMateriel ? buildMaterielLabel(selectedMateriel) : `#${selectedMaterielId}`}</span>
              : <span className="fw-normal text-muted ms-2">— choisir un engin</span>}
          </span>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" size="sm" onClick={() => fetchHistory(selectedMaterielId)} disabled={!selectedMaterielId || loadingHistory}>
              {loadingHistory ? <Spinner size="sm" /> : <FaSyncAlt />}
            </Button>
            <Button variant="outline-danger" size="sm" onClick={() => generateMaterielReparationsPDF({ context: exportContext, rows: historyRows })} disabled={!historyRows.length}>
              <FaFilePdf className="me-1" />PDF
            </Button>
            <Button variant="outline-success" size="sm" onClick={() => generateMaterielReparationsExcel({ context: exportContext, rows: historyRows })} disabled={!historyRows.length}>
              <FaFileExcel className="me-1" />Excel
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {historyError && <Alert variant="danger">{historyError}</Alert>}

          {!selectedMaterielId ? (
            <p className="text-muted mb-0">👆 Sélectionne un engin dans la section validation pour voir son historique.</p>
          ) : loadingHistory ? (
            <div className="d-flex align-items-center gap-2 text-muted"><Spinner size="sm" /> Chargement…</div>
          ) : historyRows.length === 0 ? (
            <p className="text-muted mb-0">📭 Aucun historique pour cet engin.</p>
          ) : (
            <>
              {/* KPI engin + totaux */}
              <Row className="g-3 mb-3">
                {selectedMateriel && (
                  <Col md={7}>
                    <Card style={{ background: BROWN_BG, border: `1px solid rgba(109,78,54,0.2)`, borderRadius: 10 }}>
                      <Card.Body className="py-2 px-3">
                        <div className="d-flex align-items-center justify-content-between mb-1">
                          <strong style={{ color: BROWN }}>🚜 Engin</strong>
                          <Badge style={{ background: BROWN }}>#{selectedMateriel.id}</Badge>
                        </div>
                        <Row className="g-1" style={{ fontSize: '0.85rem' }}>
                          <Col md={6}><span className="text-muted">Matricule :</span> <strong>{safeText(selectedMateriel.matricule)}</strong></Col>
                          <Col md={6}><span className="text-muted">Type :</span> <strong>{safeText(selectedMateriel.type)}</strong></Col>
                          <Col md={6}><span className="text-muted">Société :</span> <strong>{safeText(selectedMateriel.societe)}</strong></Col>
                          <Col md={6}><span className="text-muted">Chantier :</span> <strong>{safeText(selectedMateriel.chantier_actuel)}</strong></Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>
                )}
                <Col md={selectedMateriel ? 5 : 12}>
                  <Card style={{ background: '#f0f8f0', border: '1px solid #c3dfc3', borderRadius: 10 }}>
                    <Card.Body className="py-2 px-3">
                      <strong style={{ color: '#2e7d32' }}>📊 Totaux historique</strong>
                      <div className="mt-2 d-flex flex-column gap-1" style={{ fontSize: '0.9rem' }}>
                        <div className="d-flex justify-content-between"><span className="text-muted">Lignes :</span><strong>{historyRows.length}</strong></div>
                        <div className="d-flex justify-content-between"><span className="text-muted">Total HT :</span><strong>{fmtMoney(historyTotals.ht)} MAD</strong></div>
                        <div className="d-flex justify-content-between"><span className="text-muted">Total TTC :</span><strong>{fmtMoney(historyTotals.ttc)} MAD</strong></div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <BootstrapTable
                keyField="id"
                data={historyRows}
                columns={historyColumns}
                bootstrap4
                bordered={false}
                hover
                condensed
                filter={filterFactory()}
                pagination={paginationFactory({ sizePerPage: 10, showTotal: true, alwaysShowAllBtns: true })}
                noDataIndication="Aucune réparation"
                wrapperClasses="table-responsive"
              />
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default BdcReparationPage;
